import React, { createContext, useState, useEffect } from "react";

export const ThemeContext = createContext();

// Eye-friendly, professional color themes
const themeConfig = {
  blue: {
    name: "Ocean Blue",
    primary: "#3b82f6",
    primaryLight: "rgba(59, 130, 246, 0.08)",
    primaryGradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    background: "#f8fafc",
    surface: "#ffffff",
    surfaceLighter: "#f1f5f9",
    border: "#e2e8f0",
    textPrimary: "#1e293b",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  indigo: {
    name: "Royal Indigo",
    primary: "#6366f1",
    primaryLight: "rgba(99, 102, 241, 0.08)",
    primaryGradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
    background: "#fafafa",
    surface: "#ffffff",
    surfaceLighter: "#f4f4f5",
    border: "#e4e4e7",
    textPrimary: "#18181b",
    textSecondary: "#52525b",
    textMuted: "#a1a1aa",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  purple: {
    name: "Soft Purple",
    primary: "#8b5cf6",
    primaryLight: "rgba(139, 92, 246, 0.08)",
    primaryGradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    background: "#faf5ff",
    surface: "#ffffff",
    surfaceLighter: "#f5f3ff",
    border: "#e9d5ff",
    textPrimary: "#1e1b4b",
    textSecondary: "#4c1d95",
    textMuted: "#a78bfa",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  teal: {
    name: "Calm Teal",
    primary: "#14b8a6",
    primaryLight: "rgba(20, 184, 166, 0.08)",
    primaryGradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
    background: "#f0fdfa",
    surface: "#ffffff",
    surfaceLighter: "#ccfbf1",
    border: "#99f6e4",
    textPrimary: "#134e4a",
    textSecondary: "#115e59",
    textMuted: "#5eead4",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  emerald: {
    name: "Forest Green",
    primary: "#10b981",
    primaryLight: "rgba(16, 185, 129, 0.08)",
    primaryGradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    background: "#f0fdf4",
    surface: "#ffffff",
    surfaceLighter: "#d1fae5",
    border: "#a7f3d0",
    textPrimary: "#064e3b",
    textSecondary: "#065f46",
    textMuted: "#34d399",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  amber: {
    name: "Warm Amber",
    primary: "#f59e0b",
    primaryLight: "rgba(245, 158, 11, 0.08)",
    primaryGradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    background: "#fffbeb",
    surface: "#ffffff",
    surfaceLighter: "#fef3c7",
    border: "#fde68a",
    textPrimary: "#78350f",
    textSecondary: "#92400e",
    textMuted: "#fbbf24",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  slate: {
    name: "Professional Slate",
    primary: "#64748b",
    primaryLight: "rgba(100, 116, 139, 0.08)",
    primaryGradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
    background: "#f8fafc",
    surface: "#ffffff",
    surfaceLighter: "#f1f5f9",
    border: "#e2e8f0",
    textPrimary: "#0f172a",
    textSecondary: "#334155",
    textMuted: "#94a3b8",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  rose: {
    name: "Gentle Rose",
    primary: "#f43f5e",
    primaryLight: "rgba(244, 63, 94, 0.08)",
    primaryGradient: "linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)",
    background: "#fff1f2",
    surface: "#ffffff",
    surfaceLighter: "#ffe4e6",
    border: "#fecdd3",
    textPrimary: "#881337",
    textSecondary: "#9f1239",
    textMuted: "#fb7185",
    success: "#10b981",
    successLight: "rgba(16, 185, 129, 0.08)",
    error: "#ef4444",
    errorLight: "rgba(239, 68, 68, 0.08)",
    warning: "#f59e0b",
    warningLight: "rgba(245, 158, 11, 0.08)",
    info: "#06b6d4",
    infoLight: "rgba(6, 182, 212, 0.08)",
  },
  dark: {
    name: "Dark",
    primary: "#f43f5e", // Rose color for dark mode pop
    primaryLight: "rgba(244, 63, 94, 0.15)",
    primaryGradient: "linear-gradient(135deg, #f43f5e 0%, #be123c 100%)",
    background: "#0f172a",
    surface: "#1e293b",
    border: "#334155",
    textPrimary: "#f1f5f9",
    textSecondary: "#cbd5e1",
    textMuted: "#64748b",
  }
};

export const ThemeProvider = ({ children }) => {
  // LocalStorage se saved theme load karein, warna default 'blue'
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem("appTheme") || "blue";
  });

  // Jab bhi theme change ho, localStorage mein save karein
  useEffect(() => {
    localStorage.setItem("appTheme", currentTheme);
  }, [currentTheme]);

  const theme = themeConfig[currentTheme];

  return (
    <ThemeContext.Provider value={{ theme, currentTheme, setCurrentTheme, themeConfig }}>
      {children}
    </ThemeContext.Provider>
  );
};